import { streamRequest, postRequest, getRequest, deleteRequest } from './api';

/**
 * Data Service API
 */

/**
 * Stream chat with data agent
 * @param {string} question - User question
 * @param {Array} conversationHistory - Previous conversation messages
 * @param {Function} onMessage - Callback for each SSE message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 * @param {string} modelKey - Optional model key to use
 */
export async function streamDataChat(question, conversationHistory, onMessage, onError, onComplete, modelKey = null, userEmail = null, userProfile = null, sessionId = null, dashboardContext = null, chartEditorContext = null) {
  const payload = {
    message: question,
    conversation_history: conversationHistory
  };
  if (modelKey) payload.model_key = modelKey;
  if (userEmail) payload.user_email = userEmail;
  if (userProfile) payload.user_profile = userProfile;
  if (sessionId) payload.session_id = sessionId;
  if (dashboardContext) payload.dashboard_context = dashboardContext;
  if (chartEditorContext) payload.chart_editor_context = chartEditorContext;
  await streamRequest('data', '/query', payload, onMessage, onError, onComplete);
}

/**
 * Add insight to Data Gallery
 * @param {object} insight - Insight data (title, content, visualization, etc.)
 * @returns {Promise<object>} Response data
 */
export async function addToGallery(insight) {
  return await postRequest('data', '/gallery/add', insight);
}

/**
 * Get list of insights from Data Gallery
 * @returns {Promise<object>} Response data with insights
 */
export async function getGalleryInsights() {
  return await getRequest('data', '/gallery/list');
}

/**
 * Search Data Gallery
 * @param {string} query - Search query
 * @returns {Promise<object>} Response data with search results
 */
export async function searchGallery(query) {
  return await postRequest('data', '/gallery/search', { query });
}

/**
 * Get ontology relationships
 * @param {string} entity - Entity name (optional)
 * @returns {Promise<object>} Response data with relationships
 */
export async function getOntologyRelationships(entity = null) {
  const params = entity ? { views: [entity] } : { views: [] };
  return await postRequest('data', '/ontology/relationships', params);
}

/**
 * Get ontology mermaid diagram
 * @returns {Promise<object>} Response data with mermaid code
 */
export async function getOntologyMermaid() {
  return await getRequest('data', '/ontology/mermaid');
}

/**
 * Generate ontology dynamically from catalog column descriptions
 * Extracts LINK_TO relationships and builds mermaid graph
 * @returns {Promise<object>} Response data with generated mermaid code
 */
export async function generateOntology() {
  return await postRequest('data', '/ontology/generate', {});
}

/**
 * Get database catalog - list of schemas and their views
 * @param {string} dataLayer - Optional comma-separated list of schema prefixes (e.g., "REFINED_")
 * @returns {Promise<object>} Response data with database catalog
 */
export async function getCatalogDatabase(dataLayer = null) {
  const url = dataLayer ? `/catalog/database?data_layer=${encodeURIComponent(dataLayer)}` : '/catalog/database';
  return await getRequest('data', url);
}

/**
 * Get schemas catalog - detailed views and columns for specific schemas
 * @param {Array<string>} schemas - List of schema names
 * @returns {Promise<object>} Response data with schemas catalog
 */
export async function getCatalogSchemas(schemas) {
  return await postRequest('data', '/catalog/schemas', { schemas });
}

/**
 * Get views catalog - column-level metadata for specific views
 * @param {Array<string>} views - List of fully-qualified view names (SCHEMA.VIEW)
 * @param {boolean} skipValidation - Skip catalog state validation (default: true)
 * @returns {Promise<object>} Response data with views catalog
 */
export async function getCatalogViews(views, skipValidation = true) {
  return await postRequest('data', '/catalog/views', { views, skip_validation: skipValidation });
}

/**
 * Scan and cache entire database catalog
 * This performs a comprehensive scan of all schemas, views, and columns,
 * caching the results for fast future access.
 * @param {object} options - Scan options
 * @param {Array<string>} options.data_layers - Schema prefixes to scan (e.g. ["REFINED_", "RAW_"]). null = all.
 * @param {number} options.max_workers - Concurrent threads (1-8, default 3)
 * @returns {Promise<object>} Response data with scan summary
 */
export async function scanAndCacheDatabase({ data_layers = null, max_workers = 3 } = {}) {
  return await postRequest('data', '/catalog/scan', { data_layers, max_workers });
}

/**
 * Get catalog scan status
 * Poll this endpoint to check progress of background scan.
 * @returns {Promise<object>} Response data with scan status, progress, and result
 */
export async function getScanStatus() {
  return await getRequest('data', '/catalog/scan/status');
}

/**
 * Get cache metadata for all catalog levels (database, schemas, views).
 * Returns timestamps, item counts, and file sizes.
 * @returns {Promise<object>} Cache info per level
 */
export async function getCacheInfo() {
  return await getRequest('data', '/catalog/cache-info');
}

/**
 * Reset (delete) all cached catalog files.
 * @returns {Promise<object>} { status, deleted, errors }
 */
export async function resetCatalogCache() {
  return await deleteRequest('data', '/catalog/cache/reset');
}

/**
 * Reset (delete) the generated ontology file.
 * @returns {Promise<object>} { status, deleted }
 */
export async function resetOntologyCache() {
  return await deleteRequest('data', '/ontology/reset');
}

/**
 * Get database catalog from cache only (never queries Snowflake live).
 * Used by Data Explorer.
 * @param {string} dataLayer - Optional comma-separated prefixes (e.g. "REFINED_")
 * @returns {Promise<object>} { cached, schemas, database, source }
 */
export async function getCachedDatabaseCatalog(dataLayer = null) {
  const url = dataLayer
    ? `/catalog/cached/database?data_layer=${encodeURIComponent(dataLayer)}`
    : '/catalog/cached/database';
  return await getRequest('data', url);
}

/**
 * Get column details for a single view from cache only.
 * @param {string} schema - Schema name
 * @param {string} view - View name
 * @returns {Promise<object>} { cached, view: {SCHEMA, VIEW_NAME, COLUMNS, ...} }
 */
export async function getCachedViewColumns(schema, view) {
  return await getRequest('data', `/catalog/cached/views?schema=${encodeURIComponent(schema)}&view=${encodeURIComponent(view)}`);
}

/**
 * Get agent information (system prompt and tools)
 * @returns {Promise<object>} Response data with agent info
 */
export async function getAgentInfo() {
  return await getRequest('data', '/agent/info');
}

export async function getAgentInfoWithContext(dashboardContext = null, chartEditorContext = null) {
  return await postRequest('data', '/agent/info', {
    dashboard_context: dashboardContext,
    chart_editor_context: chartEditorContext,
  });
}

/**
 * Get current sync schedule configuration
 * @returns {Promise<object>} Schedule config { enabled, frequency, hour, minute, data_layers, max_workers }
 */
export async function getScheduleConfig() {
  return await getRequest('data', '/catalog/schedule');
}

/**
 * Update sync schedule configuration
 * @param {object} config - Schedule configuration
 * @param {boolean} config.enabled - Whether schedule is enabled
 * @param {string} config.frequency - "daily" or "weekly"
 * @param {number} config.hour - Hour to run (0-23)
 * @param {number} config.minute - Minute to run (0-59)
 * @param {Array<string>} config.data_layers - Schema prefixes or null for all
 * @param {number} config.max_workers - Concurrent threads (1-8)
 * @returns {Promise<object>} { status, config }
 */
export async function setScheduleConfig(config) {
  return await postRequest('data', '/catalog/schedule', config);
}

/**
 * Get Data Agent Q&A history
 * @param {object} filters - Filter parameters (limit, session_id, user)
 * @returns {Promise<object>} Response data with history records
 */
export async function getDataQAHistory(filters = {}) {
  return await getRequest('data', '/qa/history', filters);
}

/**
 * Submit feedback for a Data Agent Q&A
 * @param {number} qaId - Q&A record ID
 * @param {string} feedback - 'correct' or 'incorrect'
 * @returns {Promise<object>} Response data
 */
export async function submitDataFeedback(qaId, feedback) {
  return await postRequest('data', '/qa/feedback', { record_id: qaId, feedback });
}

/**
 * Submit quick-reaction feedback (like/dislike) for a Data Agent Q&A
 * @param {number} qaId - Q&A record ID
 * @param {string|null} feedbackType - 'like', 'dislike', or null to clear
 * @returns {Promise<object>} Response data
 */
export async function submitDataFeedbackType(qaId, feedbackType) {
  return await postRequest('data', '/qa/feedback', { record_id: qaId, feedback_type: feedbackType || '' });
}

/**
 * Get Data Agent Q&A statistics
 * @returns {Promise<object>} Statistics data
 */
export async function getDataQAStatistics() {
  return await getRequest('data', '/qa/statistics');
}

/**
 * Mark a Data Agent Q&A answer as correct or incorrect
 * @param {number} qaId - Q&A record ID
 * @param {boolean} isCorrect - True if correct, false if incorrect
 * @returns {Promise<object>} Response data
 */
export async function markDataQAAnswer(qaId, isCorrect) {
  return await postRequest('data', '/qa/mark', { qa_id: qaId, is_correct: isCorrect });
}

// ── Gallery Indexer ────────────────────────────────────────────────

/**
 * Get gallery indexer job status
 * @returns {Promise<object>} { status, last_run, last_summary, next_run, is_paused }
 */
export async function getGalleryIndexerStatus() {
  return await getRequest('data', '/gallery/indexer/status');
}

/**
 * Get gallery indexer run history
 * @param {number} limit - Max runs to return (default 20)
 * @returns {Promise<object>} { runs: [...] }
 */
export async function getGalleryIndexerHistory(limit = 20) {
  return await getRequest('data', `/gallery/indexer/history?limit=${limit}`);
}

/**
 * Trigger an immediate gallery index run
 * @returns {Promise<object>} { status, message }
 */
export async function triggerGalleryIndexer() {
  return await postRequest('data', '/gallery/indexer/run', {});
}

/**
 * Pause the gallery indexer scheduled job
 * @returns {Promise<object>} { status }
 */
export async function pauseGalleryIndexer() {
  return await postRequest('data', '/gallery/indexer/pause', {});
}

/**
 * Resume the gallery indexer scheduled job
 * @returns {Promise<object>} { status, next_run }
 */
export async function resumeGalleryIndexer() {
  return await postRequest('data', '/gallery/indexer/resume', {});
}

// ── Gallery RAG Config ─────────────────────────────────────────────

/**
 * Get current gallery RAG configuration
 * @returns {Promise<object>} { rag_enabled, rag_top_k, rag_similarity_threshold }
 */
export async function getGalleryRagConfig() {
  return await getRequest('data', '/gallery/rag/config');
}

/**
 * Update gallery RAG configuration
 * @param {object} config - { rag_enabled, rag_top_k, rag_similarity_threshold }
 * @returns {Promise<object>} Updated config
 */
export async function updateGalleryRagConfig(config) {
  return await postRequest('data', '/gallery/rag/config', config);
}

// ── Gallery Schedule Config ────────────────────────────────────────

/**
 * Get current gallery indexer schedule configuration
 * @returns {Promise<object>} { interval_seconds, is_paused, next_run }
 */
export async function getGalleryScheduleConfig() {
  return await getRequest('data', '/gallery/schedule/config');
}

/**
 * Update gallery indexer schedule interval
 * @param {object} config - { interval_seconds }
 * @returns {Promise<object>} Updated schedule config
 */
export async function updateGalleryScheduleConfig(config) {
  return await postRequest('data', '/gallery/schedule/config', config);
}

/**
 * Trigger a vector-only rebuild (no data sync)
 * @returns {Promise<object>} { status, message }
 */
export async function triggerGalleryVectorRebuild() {
  return await postRequest('data', '/gallery/vector/rebuild', {});
}

/**
 * Execute a SQL query against Snowflake (read-only SELECT only)
 * @param {string} sql - The SQL query to execute
 * @param {number} limit - Max rows to return (default 1000, max 1000)
 * @param {boolean} skipCache - Bypass cache for fresh data
 * @returns {Promise<object>} { status, data: { rows, columns, column_types, row_count }, cached }
 */
export async function executeSql(sql, limit = 1000, skipCache = false) {
  return await postRequest('data', '/execute-sql', { sql, limit, skip_cache: skipCache });
}

/**
 * Get GraphQL Builder schema - views and relationships from ontology
 * @returns {Promise<object>} { views, relationships }
 */
export async function getGraphQLSchema() {
  return await getRequest('data', '/graphql/schema');
}

/**
 * Execute a GraphQL Builder query (structured JSON query)
 * @param {object} queryPayload - The { query: { table, fields, expand, filters, limit } } object
 * @returns {Promise<object>} { data: [...nested rows], meta: { row_count, truncated, generated_sql } }
 */
export async function executeGraphQLQuery(queryPayload) {
  const q = encodeURIComponent(queryPayload.query || '');
  return await getRequest('data', `/graphql/execute?query=${q}`);
}

/**
 * Get full data model for ERD visualization (tables, columns, relationships)
 * @returns {Promise<object>} { tables, relationships, empty }
 */
export async function getDataModel() {
  return await getRequest('data', '/catalog/data-model');
}

/**
 * Get the user's existing Graph API key info (masked)
 */
export async function getGraphQLApiKey(userEmail) {
  return await getRequest('data', `/graphql/api-key?user_email=${encodeURIComponent(userEmail)}`);
}

/**
 * Create or reset a Graph API key
 */
export async function createGraphQLApiKey(userEmail) {
  return await postRequest('data', '/graphql/api-key', { user_email: userEmail });
}

/**
 * Delete a Graph API key
 */
export async function deleteGraphQLApiKey(userEmail) {
  return await deleteRequest('data', '/graphql/api-key', { user_email: userEmail });
}

// ── Conversation History ───────────────────────────────────────────

/**
 * Get list of distinct conversations for the Data Agent
 * @param {object} params - { user, limit, search }
 * @returns {Promise<object>} { conversations, total_count }
 */
export async function getDataConversations(params = {}) {
  const query = new URLSearchParams();
  if (params.user) query.set('user', params.user);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.search) query.set('search', params.search);
  const qs = query.toString();
  return await getRequest('data', `/qa/conversations${qs ? '?' + qs : ''}`);
}

/**
 * Get all messages for a specific conversation
 * @param {string} sessionId - The session_id of the conversation
 * @returns {Promise<object>} { session_id, messages }
 */
export async function getDataConversationMessages(sessionId) {
  return await getRequest('data', `/qa/conversations/${encodeURIComponent(sessionId)}`);
}

const dataService = {
  streamDataChat,
  addToGallery,
  getGalleryInsights,
  searchGallery,
  getOntologyRelationships,
  getOntologyMermaid,
  generateOntology,
  getCatalogDatabase,
  getCatalogSchemas,
  getCatalogViews,
  scanAndCacheDatabase,
  getScanStatus,
  getCacheInfo,
  getCachedDatabaseCatalog,
  getCachedViewColumns,
  getAgentInfo,
  getScheduleConfig,
  setScheduleConfig,
  getDataQAHistory,
  submitDataFeedback,
  submitDataFeedbackType,
  getDataQAStatistics,
  markDataQAAnswer,
  getDataConversations,
  getDataConversationMessages,
  getGraphQLSchema,
  executeGraphQLQuery,
  getGraphQLApiKey,
  createGraphQLApiKey,
  deleteGraphQLApiKey,
};

export default dataService;
